import React, { useState, useMemo } from 'react';
import {
  Modal,
  Table,
  TextInput,
  Stack,
  Group,
  Text,
  RingProgress,
  Code,
  ScrollArea,
  Badge,
  Tooltip,
  Box,
  Paper,
  Divider,
  Collapse,
  ActionIcon,
  Flex,
  LoadingOverlay,
} from '@mantine/core';
import { IconSearch, IconDatabase, IconChevronDown, IconChevronRight, IconX } from '@tabler/icons';
import { ExternalObjectConf } from '@bageltechnologies/bagel-common';
import { useIntegrationExploreFields } from '../../../../hooks/useIntegrations';
import { OverflownText } from '../../../OverflownText';
import { useDebouncedValue } from '@mantine/hooks';

interface ExploreFieldsModalProps {
  opened: boolean;
  onClose: () => void;
  externalObject: ExternalObjectConf | null;
  integrationName: string;
  integrationKey: string;
}

interface FieldMetaWithStats {
  name: string;
  key: string;
  label: string;
  type: string;
  required?: boolean;
  length?: number;
  fillPercentage?: number;
  hasStatistics?: boolean;
  totalRecords?: number;
  nonNullCount?: number;
  types?: string[];
  lastCalculated?: string;
  // Additional field metadata for expanded view
  values?: string[];
  uniqueValueCount?: number;
  minValue?: string | number;
  maxValue?: string | number;
  commonValues?: Array<{ value: string; count: number; percentage: number }>;
}

export const ExploreFieldsModal: React.FC<ExploreFieldsModalProps> = ({
  opened,
  onClose,
  externalObject,
  integrationName,
  integrationKey,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Use the new API to fetch complete field exploration data
  const {
    data: exploreData,
    isLoading: isLoadingExploreData,
    isError: isExploreDataError,
    error: exploreDataError
  } = useIntegrationExploreFields(
    integrationKey,
    externalObject?.bagelObjectKey || '',
    externalObject?.originObject || '',
    opened // Only fetch when modal is opened
  );

  // Always start with existing fieldsMeta for immediate display, then enhance with API data
  const fieldsData = useMemo(() => {
    // Start with existing fieldsMeta data for immediate display
    const baseFields = externalObject?.fieldsMeta || [];
    
    // If we don't have any existing data, return empty array
    if (baseFields.length === 0) return [];
    
    // Map existing fields to our format
    const existingFields = baseFields.map((field: any) => ({
      name: field.name || field.key,
      key: field.key,
      label: field.label || field.key || field.name,
      type: field.type || 'unknown',
      required: field.required,
      length: field.length,
      fillPercentage: field.fillPercentage,
      hasStatistics: field.hasStatistics,
      totalRecords: field.totalRecords,
      nonNullCount: field.nonNullCount,
      types: field.types,
      lastCalculated: field.lastCalculated,
      values: (field as any).values,
      uniqueValueCount: (field as any).uniqueValueCount,
      minValue: (field as any).minValue,
      maxValue: (field as any).maxValue,
      commonValues: (field as any).commonValues,
    } as FieldMetaWithStats));

    // If we have enhanced data from the explore API, merge it with existing data
    if (exploreData?.fields) {
      const enhancedFieldsMap = new Map(
        exploreData.fields.map(field => [field.key, field])
      );
      
      return existingFields.map(existingField => {
        const enhancedField = enhancedFieldsMap.get(existingField.key);
        if (enhancedField) {
          // Merge enhanced data with existing field
          return {
            ...existingField,
            name: enhancedField.name || existingField.name,
            label: enhancedField.label || existingField.label,
            type: enhancedField.type || existingField.type,
            required: enhancedField.required ?? existingField.required,
            length: enhancedField.length ?? existingField.length,
            fillPercentage: enhancedField.fillPercentage ?? existingField.fillPercentage,
            hasStatistics: enhancedField.hasStatistics ?? existingField.hasStatistics,
            totalRecords: enhancedField.totalRecords ?? existingField.totalRecords,
            nonNullCount: enhancedField.nonNullCount ?? existingField.nonNullCount,
            types: enhancedField.types || existingField.types,
            lastCalculated: enhancedField.lastCalculated || existingField.lastCalculated,
            values: enhancedField.values || existingField.values,
            uniqueValueCount: enhancedField.uniqueValueCount ?? existingField.uniqueValueCount,
            minValue: enhancedField.min ?? existingField.minValue,
            maxValue: enhancedField.max ?? existingField.maxValue,
            commonValues: enhancedField.commonValues || existingField.commonValues,
          } as FieldMetaWithStats;
        }
        return existingField;
      });
    }
    
    // Return existing fields if no enhanced data yet
    return existingFields;
  }, [externalObject, exploreData]);

  // Filter fields based on debounced search query
  const filteredFields = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return fieldsData;
    
    const query = debouncedSearchQuery.toLowerCase();
    return fieldsData.filter(field => 
      field.name.toLowerCase().includes(query) ||
      field.label.toLowerCase().includes(query) ||
      field.key.toLowerCase().includes(query) ||
      field.type.toLowerCase().includes(query)
    );
  }, [fieldsData, debouncedSearchQuery]);

  // Calculate statistics - use API data if available, otherwise calculate from fields
  const stats = useMemo(() => {
    if (exploreData) {
      return {
        totalFields: exploreData.totalFields,
        fieldsWithStats: exploreData.fieldsWithStatistics,
        requiredFields: fieldsData.filter(f => f.required).length,
        averageFillRate: fieldsData.length > 0 
          ? fieldsData.reduce((sum, f) => sum + (f.fillPercentage || 0), 0) / fieldsData.length 
          : 0,
        totalDataRecords: exploreData.totalDataRecords,
        lastUpdated: exploreData.lastUpdated,
      };
    }

    // Fallback calculation
    const totalFields = fieldsData.length;
    const fieldsWithStats = fieldsData.filter(f => f.hasStatistics).length;
    const requiredFields = fieldsData.filter(f => f.required).length;
    const averageFillRate = fieldsData.length > 0 
      ? fieldsData.reduce((sum, f) => sum + (f.fillPercentage || 0), 0) / fieldsData.length 
      : 0;

    return {
      totalFields,
      fieldsWithStats,
      requiredFields,
      averageFillRate,
    };
  }, [exploreData, fieldsData]);

  const formatNumber = (num?: number) => {
    if (num === undefined || num === null) return '-';
    return num.toLocaleString();
  };

  const formatPercentage = (percentage?: number) => {
    if (percentage === undefined || percentage === null) return null;
    return Math.round(percentage);
  };

  const getFillColor = (percentage?: number) => {
    if (!percentage) return 'gray';
    return percentage > 80 ? 'green' : percentage > 50 ? 'yellow' : 'red';
  };

  const toggleRowExpansion = (fieldKey: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fieldKey)) {
        newSet.delete(fieldKey);
      } else {
        newSet.add(fieldKey);
      }
      return newSet;
    });
  };

  const renderExpandedContent = (field: FieldMetaWithStats) => {
    // console.log({FieldMetaWithStats:field});
    return (
      <Box p="sm" bg="gray.0">
        <Stack spacing="sm">
          <Text size="sm" weight={500} mb="xs">Field Details</Text>
          
          <Group spacing="xl" align="flex-start">
            {/* Sample Values */}
            {field.values && field.values.length > 0 && (
              <Box>
                <Text size="xs" weight={500} color="dimmed" mb={4}>Sample Values</Text>
                <Stack spacing={2}>
                  {field.values.slice(0, 5).map((value, index) => (
                    <Code key={index}>{String(value)}</Code>
                  ))}
                  {field.values.length > 5 && (
                    <Text size="xs" color="dimmed">...and {field.values.length - 5} more</Text>
                  )}
                </Stack>
              </Box>
            )}

            {/* Statistics */}
            {(field.uniqueValueCount !== undefined || field.minValue !== undefined || field.maxValue !== undefined) && (
              <Box>
                <Text size="xs" weight={500} color="dimmed" mb={4}>Statistics</Text>
                <Stack spacing={2}>
                  {field.uniqueValueCount !== undefined && (
                    <Text size="xs"><strong>Unique Values:</strong> {formatNumber(field.uniqueValueCount)}</Text>
                  )}
                  {field.minValue !== undefined && (
                    <Text size="xs"><strong>Min Value:</strong> {String(field.minValue)}</Text>
                  )}
                  {field.maxValue !== undefined && (
                    <Text size="xs"><strong>Max Value:</strong> {String(field.maxValue)}</Text>
                  )}
                </Stack>
              </Box>
            )}

            {/* Common Values */}
            {field.commonValues && field.commonValues.length > 0 && (
              <Box>
                <Text size="xs" weight={500} color="dimmed" mb={4}>Most Common Values</Text>
                <Stack spacing={2}>
                  {field.commonValues.slice(0, 5).map((item, index) => (
                    <Group key={index} spacing="xs" noWrap>
                      <Code>{String(item.value)}</Code>
                      <Text size="xs" color="dimmed">
                        {item.count} ({item.percentage.toFixed(1)}%)
                      </Text>
                    </Group>
                  ))}
                  {field.commonValues.length > 5 && (
                    <Text size="xs" color="dimmed">...and {field.commonValues.length - 5} more</Text>
                  )}
                </Stack>
              </Box>
            )}

            {/* Additional Info */}
            <Box>
              <Text size="xs" weight={500} color="dimmed" mb={4}>Additional Info</Text>
              <Stack spacing={2}>
                {field.lastCalculated && (
                  <Text size="xs"><strong>Last Calculated:</strong> {new Date(field.lastCalculated).toLocaleString()}</Text>
                )}
                {field.hasStatistics && (
                  <Badge size="xs" color="blue" variant="dot">Statistical Data Available</Badge>
                )}
                {field.required && (
                  <Badge size="xs" color="red" variant="outline">Required Field</Badge>
                )}
              </Stack>
            </Box>
          </Group>
        </Stack>
      </Box>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size="xl"
      padding={0}
    >
      <Box>
        <Group p="sm" mb="sm" position='apart' sx={{borderBottom: '1px solid #e0e0e0'}}>
          <Group spacing="sm">
            <IconDatabase size={20} />
            <Stack spacing={0}>
              <Text weight={500}>Explore Fields - {externalObject?.label}</Text>
              {isLoadingExploreData && (
                <Text size="xs" color="dimmed">Loading enhanced field data...</Text>
              )}
              {exploreData && !isLoadingExploreData && (
                <Text size="xs" color="green">Enhanced data loaded</Text>
              )}
              {isExploreDataError && (
                <Text size="xs" color="orange">Using cached field data</Text>
              )}
            </Stack>
          </Group>
          <ActionIcon variant="subtle" size="sm" onClick={onClose}>
            <IconX size={16} />
          </ActionIcon>
        </Group>
        
        <Stack spacing="md">
          {/* Summary Statistics */}
          <Paper mx="sm" withBorder p="sm" bg="gray.0">
            <Group position="apart">
              <Stack spacing={4}>
                <Text size="xs" color="dimmed">Total Fields</Text>
                <Text weight={500}>{stats.totalFields}</Text>
              </Stack>
              <Stack spacing={4}>
                <Text size="xs" color="dimmed">With Statistics</Text>
                <Text weight={500}>{stats.fieldsWithStats}</Text>
              </Stack>
              <Stack spacing={4}>
                <Text size="xs" color="dimmed">Required Fields</Text>
                <Text weight={500}>{stats.requiredFields}</Text>
              </Stack>
              <Stack spacing={4}>
                <Text size="xs" color="dimmed">Avg Fill Rate</Text>
                <Text weight={500}>{Math.round(stats.averageFillRate)}%</Text>
              </Stack>
            </Group>
          </Paper>

          {/* Search Input */}
          <TextInput mx="sm"
            placeholder="Search fields by name, label, key, or type..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.currentTarget.value)}
            icon={<IconSearch size={16} />}
            rightSection={
              searchQuery && (
                <Text size="xs" color="dimmed" pr="sm">
                  {filteredFields.length} of {fieldsData.length}
                </Text>
              )
            }
          />

          {/* Fields Table */}
          <ScrollArea style={{ height: 400 }} sx={{
            borderTop: '1px solid #e0e0e0',
            borderBottom: '1px solid #e0e0e0',
          }}>
            <Table striped highlightOnHover fontSize="sm">
              <thead style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'white' }}>
                <tr>
                  <th style={{ width: '24px', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 11 }}></th>
                  <th style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 11 }}>Field</th>
                  <th style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 11 }}>Type</th>
                  <th style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 11 }}>Fill Rate</th>
                  <th style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 11 }}>Records</th>
                  <th style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 11 }}>Properties</th>
                </tr>
              </thead>
              <tbody>
                {filteredFields.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <Text color="dimmed" align="center" py="xl">
                        {searchQuery ? 'No fields found matching your search.' : 'No fields available.'}
                      </Text>
                    </td>
                  </tr>
                ) : (
                  filteredFields.map((field) => {
                    const isExpanded = expandedRows.has(field.key);
                    return (
                      <React.Fragment key={field.key}>
                        <tr style={{ cursor: 'pointer' }} onClick={() => toggleRowExpansion(field.key)}>
                          <td style={{ width: '24px', textAlign: 'center' }}>
                            <ActionIcon variant="subtle" size="sm">
                              {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                            </ActionIcon>
                          </td>
                          <td>
                            <Stack spacing={2} maw={"350px"}>
                              <OverflownText weight={500} size="sm">
                                {field.label}
                              </OverflownText>
                              
                              {field.label !== field.key && (
                                <OverflownText color="dimmed" weight={500} size="sm">
                                  {field.key}
                                </OverflownText>
                              )}
                            </Stack>
                          </td>
                          <td>
                            <Group spacing={4}>
                              <Badge size="xs" variant="outline">
                                {field.type}
                              </Badge>
                              {field.types && field.types.length > 1 && (
                                <Tooltip label={`Multiple types: ${field.types.join(', ')}`}>
                                  <Badge size="xs" color="blue" variant="dot">
                                  +{field.types.length - 1}
                                  </Badge>
                                </Tooltip>
                              )}
                            </Group>
                          </td>
                          <td>
                            {field.fillPercentage !== undefined && field.fillPercentage !== null ? (
                              <Group spacing="sm" noWrap>
                                <RingProgress
                                  size={24}
                                  thickness={3}
                                  sections={[
                                    {
                                      value: field.fillPercentage,
                                      color: getFillColor(field.fillPercentage)
                                    }
                                  ]}
                                />
                                <Text size="xs" color="dimmed">
                                  {formatPercentage(field.fillPercentage)}%
                                </Text>
                              </Group>
                            ) : (
                              <Text size="xs" color="dimmed">N/A</Text>
                            )}
                          </td>
                          <td>
                            {field.totalRecords && field.nonNullCount !== undefined ? (
                              <Stack spacing={2}>
                                <Text size="xs">
                                  {formatNumber(field.nonNullCount)} / {formatNumber(field.totalRecords)}
                                </Text>
                                <Text size="xs" color="dimmed">
                                  {formatNumber(field.totalRecords - field.nonNullCount)} null
                                </Text>
                              </Stack>
                            ) : (
                              <Text size="xs" color="dimmed">-</Text>
                            )}
                          </td>
                          <td>
                            <Group spacing={4}>
                              {field.required && (
                                <Badge size="xs" color="red" variant="outline">
                                Required
                                </Badge>
                              )}
                              {field.length && (
                                <Tooltip label="Maximum length">
                                  <Badge size="xs" color="gray" variant="outline">
                                    {field.length}
                                  </Badge>
                                </Tooltip>
                              )}
                              {field.hasStatistics && (
                                <Tooltip label="Has statistical data">
                                  <Badge size="xs" color="blue" variant="dot">
                                  Stats
                                  </Badge>
                                </Tooltip>
                              )}
                            </Group>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} style={{ padding: 0 }}>
                              <Collapse in={isExpanded}>
                                {renderExpandedContent(field)}
                              </Collapse>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </ScrollArea>
        </Stack>
        <Box m="sm">
          {filteredFields.length > 0 && (
            <Text size="xs" color="dimmed" align="center">
            Showing {filteredFields.length} of {fieldsData.length} fields
              {stats.lastUpdated && (
                <> • Last updated: {new Date(stats.lastUpdated).toLocaleDateString()}</>
              )}
              {stats.totalDataRecords && (
                <> • Total records: {formatNumber(stats.totalDataRecords)}</>
              )}
              {isExploreDataError && (
                <> • Using cached field data</>
              )}
            </Text>
          )}
        </Box>
      </Box>
    </Modal>
  );
}; 